import { FastbootError } from "../fastboot/errors";
import { grantedFastbootDevices, requestFastbootDevice, WebUsbTransport, type FastbootTransport } from "../fastboot/transport";

/** How the runner obtains a transport: a user-gesture request, or a wait for a reboot to finish. */
export interface DeviceProvider {
  /** Must be called from a click handler in the browser. */
  request(): Promise<FastbootTransport>;
  /** Wait until the device with `serial` (or any granted fastboot device if undefined) re-enumerates. */
  waitFor(serial: string | undefined, timeoutMs: number): Promise<FastbootTransport>;
}

export class WebUsbDeviceProvider implements DeviceProvider {
  constructor(
    private readonly nav: Navigator = navigator,
    private readonly sleep: (ms: number) => Promise<void> = (ms) => new Promise((r) => setTimeout(r, ms)),
  ) {}

  async request(): Promise<FastbootTransport> {
    const dev = await requestFastbootDevice(this.nav);
    const t = new WebUsbTransport(dev);
    await t.open();
    return t;
  }

  async waitFor(serial: string | undefined, timeoutMs: number): Promise<FastbootTransport> {
    const deadline = Date.now() + timeoutMs;
    // Give the OS a moment to drop the old handle before we poll.
    await this.sleep(1500);
    while (Date.now() < deadline) {
      const devs = await grantedFastbootDevices(this.nav);
      const match = devs.find((d) => serial === undefined || d.serialNumber === serial) ?? (devs.length === 1 ? devs[0] : undefined);
      if (match) {
        const t = new WebUsbTransport(match);
        try {
          await t.open();
          return t;
        } catch {
          // Not ready yet (driver re-binding); keep polling.
          await t.close();
        }
      }
      await this.sleep(500);
    }
    throw new FastbootError("disconnected", `phone did not reappear in fastboot within ${Math.round(timeoutMs / 1000)} s`);
  }
}
